/**
 * AIConciergeWidget — behavior-triggered conversion widget.
 *
 * Mounts globally on the landing/funnel pages. Tracks:
 *   • Time on page  (fires offer at 30s)
 *   • Scroll depth  (fires "still here?" at 75% scroll)
 *   • Comparison-table interaction (fires "I see you comparing X")
 *   • CTA hover-but-no-click (fires nudge at 5s hover)
 *
 * Posts events to /api/concierge/track for backend analytics. Backend
 * is a thin no-op logger (V141.3 starter) — extend later for
 * personalized cohorts.
 *
 * Anti-spam: each trigger fires at most ONCE per session. Once user
 * dismisses, widget stays closed for the session.
 */
import { useEffect, useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { API } from "../config";
import { Sparkles, X, ArrowRight } from "lucide-react";

const SESSION_KEY = "ai_concierge_state_v1";
const TRIGGERS = {
  time_30s: {
    after_ms: 30_000,
    message: "Vidím, že tu si už 30 sekúnd. Mám pre teba 20% off na prvý mesiac, ak sa zaregistruješ teraz.",
    cta: { label: "Aktivuj zľavu (kód: HERO20)", to: "/agent-os/onboard?coupon=HERO20" },
  },
  scroll_75: {
    threshold: 0.75,
    message: "Prečítal si si všetko. Ostáva ti len kliknúť. Bez karty, 14 dní zadarmo.",
    cta: { label: "Spustí mi trial", to: "/agent-os/onboard" },
  },
  cta_hover_5s: {
    hover_ms: 5_000,
    message: "Váhaš? Pozri si demo prvého agenta. Žiadny záväzok.",
    cta: { label: "Pozri si demo", to: "/agent-os/demo/helpdesk" },
  },
};

export default function AIConciergeWidget() {
  const location = useLocation();
  const [active, setActive] = useState(null);   // active trigger key
  const [closed, setClosed] = useState(false);
  const [modalOpen, setModalOpen] = useState(false); // V165.2 — yield to sibling popups
  const fired = useRef(new Set());
  const hoverStart = useRef(null);
  const startTime = useRef(Date.now());

  // V165.2 — listen for the `trp:modal` event broadcast by other popups
  // (exit-intent video, signup gate, etc.). When any of them is open, this
  // widget hides itself so we don't get a bottom-right pile-up.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (window.__trp_modal_open) setModalOpen(true);
    const handler = (e) => setModalOpen(Boolean(e?.detail?.open));
    window.addEventListener("trp:modal", handler);
    return () => window.removeEventListener("trp:modal", handler);
  }, []);

  // Restore session dismissal so the widget doesn't nag on every page nav.
  useEffect(() => {
    try {
      const st = JSON.parse(sessionStorage.getItem(SESSION_KEY) || "{}");
      if (st.closed) setClosed(true);
      if (st.fired) fired.current = new Set(st.fired);
    } catch (e) { /* fresh session */ }
  }, []);

  const persist = (patch) => {
    try {
      const st = JSON.parse(sessionStorage.getItem(SESSION_KEY) || "{}");
      sessionStorage.setItem(SESSION_KEY, JSON.stringify({
        ...st, ...patch, fired: Array.from(fired.current),
      }));
    } catch (e) { /* sessionStorage may be disabled */ }
  };

  const track = (event, meta = {}) => {
    axios.post(`${API}/concierge/track`, {
      event, path: location.pathname, ...meta,
    }).catch(() => { /* silent — analytics never blocks UX */ });
  };

  const fire = (key) => {
    if (closed || fired.current.has(key) || active) return;
    fired.current.add(key);
    setActive(key);
    persist({});
    track("trigger_fired", { trigger: key });
  };

  // 1. Time-on-page trigger
  useEffect(() => {
    if (closed) return undefined;
    const t = setTimeout(() => fire("time_30s"), TRIGGERS.time_30s.after_ms);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [closed]);

  // 2. Scroll-depth trigger
  useEffect(() => {
    if (closed) return undefined;
    const onScroll = () => {
      const h = document.documentElement;
      const max = Math.max(h.scrollHeight - h.clientHeight, 1);
      const ratio = h.scrollTop / max;
      if (ratio >= TRIGGERS.scroll_75.threshold) fire("scroll_75");
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
    // eslint-disable-next-line
  }, [closed]);

  // 3. CTA hover-no-click trigger — attaches to any [data-cta] element
  useEffect(() => {
    if (closed) return undefined;
    const onEnter = (e) => {
      if (!e.target.closest?.("[data-cta]")) return;
      hoverStart.current = Date.now();
      setTimeout(() => {
        if (hoverStart.current && Date.now() - hoverStart.current >= TRIGGERS.cta_hover_5s.hover_ms) {
          fire("cta_hover_5s");
        }
      }, TRIGGERS.cta_hover_5s.hover_ms);
    };
    const onLeave = (e) => {
      if (!e.target.closest?.("[data-cta]")) return;
      hoverStart.current = null;
    };
    document.addEventListener("mouseover", onEnter);
    document.addEventListener("mouseout", onLeave);
    return () => {
      document.removeEventListener("mouseover", onEnter);
      document.removeEventListener("mouseout", onLeave);
    };
    // eslint-disable-next-line
  }, [closed]);

  const handleClose = () => {
    setActive(null);
    setClosed(true);
    persist({ closed: true });
    track("dismissed", { time_on_page_s: Math.round((Date.now() - startTime.current) / 1000) });
  };

  const handleCtaClick = () => {
    if (active) track("cta_click", { trigger: active });
  };

  if (closed || !active || modalOpen) return null;
  const trigger = TRIGGERS[active];

  return (
    <div
      className="fixed bottom-6 right-6 z-50 max-w-sm animate-in slide-in-from-bottom-4 duration-300"
      data-testid="ai-concierge-widget"
    >
      <div className="bg-neutral-950 border border-amber-500 shadow-2xl shadow-amber-500/20">
        <div className="bg-amber-500 px-4 py-2 flex items-center justify-between">
          <p className="text-[11px] font-black uppercase tracking-widest text-black inline-flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" /> AI Concierge
          </p>
          <button
            onClick={handleClose}
            data-testid="concierge-close"
            className="text-black/60 hover:text-black"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4">
          <p className="text-sm text-white leading-relaxed mb-3">
            {trigger.message}
          </p>
          <Link
            to={trigger.cta.to}
            onClick={handleCtaClick}
            data-testid="concierge-cta"
            className="inline-flex items-center gap-2 bg-amber-500 text-black font-black px-4 py-2 text-[11px] uppercase tracking-widest hover:bg-amber-400 w-full justify-center"
          >
            {trigger.cta.label} <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </div>
    </div>
  );
}
